import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getInteractiveLesson, updateInteractiveLesson, deleteInteractiveLesson } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './InteractiveLessonEditor.css';

const InteractiveLessonEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const fetchLesson = async () => {
      try {
        setLoading(true);
        const response = await handleRequest(getInteractiveLesson, id);
        setLesson(response.data);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste leksjon');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLesson();
    }
  }, [id, user, navigate, handleRequest]);

  const handleSave = async () => {
    if (!lesson) return;

    setSaving(true);
    setErrorMessage('');

    try {
      await handleRequest(updateInteractiveLesson, id, {
        title: lesson.title,
        topic: lesson.topic,
        subjectArea: lesson.subjectArea,
        learnerProfile: lesson.learnerProfile,
        durationMinutes: lesson.durationMinutes,
        coursePosition: lesson.coursePosition,
        lessonLanguage: lesson.lessonLanguage,
        lessonData: lesson.lessonData
      });
      // Show success message or navigate
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke lagre leksjon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Er du sikker på at du vil slette denne leksjonen?')) return;

    try {
      await handleRequest(deleteInteractiveLesson, id);
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke slette leksjon');
    }
  };

  const handleView = () => {
    navigate(`/interactive-lessons/${id}`);
  };

  if (loading) {
    return (
      <div className="interactive-lesson-editor">
        <div className="loading">Laster leksjon...</div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="interactive-lesson-editor">
        <ErrorCard message="Leksjon ikke funnet" />
        <button onClick={() => navigate('/dashboard')} className="back-button">
          Tilbake til hovedsiden
        </button>
      </div>
    );
  }

  return (
    <div className="interactive-lesson-editor">
      <div className="editor-container">
        <div className="editor-header">
          <div>
            <h1>{lesson.title || lesson.lessonData?.lessonTitle}</h1>
            <p className="lesson-meta">
              Emne: {lesson.topic} | Fagområde: {lesson.subjectArea} | Varighet: {lesson.durationMinutes} min
            </p>
            {lesson.sourceCompetenceAim?.delmalTitle && (
              <p className="lesson-meta">
                Basert på delmål: {lesson.sourceCompetenceAim.delmalTitle}
                {lesson.sourceCompetenceAim.competenceAimTitle ? ` (Kompetansemål: ${lesson.sourceCompetenceAim.competenceAimTitle})` : ''}
              </p>
            )}
          </div>
          <div className="header-actions">
            <button onClick={handleView} className="view-button">
              Se leksjon
            </button>
            <button onClick={handleDelete} className="delete-button">
              Slett leksjon
            </button>
          </div>
        </div>

        {errorMessage && <ErrorCard message={errorMessage} />}

        <div className="editor-content">
          <div className="lesson-info">
            <h2>Leksjonsinformasjon</h2>
            <p><strong>Læringsmål:</strong></p>
            <ul>
              {lesson.lessonData?.learningObjectives?.map((obj, index) => (
                <li key={index}>{obj}</li>
              ))}
            </ul>
          </div>

          <div className="screens-preview">
            <h2>Skjermer ({lesson.lessonData?.screens?.length || 0})</h2>
            <div className="screens-list">
              {lesson.lessonData?.screens?.map((screen, index) => (
                <div key={screen.id || index} className="screen-preview">
                  <div className="screen-header">
                    <span className="screen-number">{index + 1}</span>
                    <span className="screen-type">{screen.type}</span>
                    <span className="screen-title">{screen.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="editor-actions">
            <button onClick={() => navigate('/dashboard')} className="cancel-button">
              Avbryt
            </button>
            <button onClick={handleSave} className="save-button" disabled={saving}>
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveLessonEditor;
